import { Package, CheckCircle, Zap, DollarSign, TrendingUp } from 'lucide-react';
import { ContributorArcheDashboardSummary } from '../../lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

interface MetricsSummaryCardsProps {
  summary: ContributorArcheDashboardSummary | null;
  loading: boolean;
}

export function MetricsSummaryCards({ summary, loading }: MetricsSummaryCardsProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {[...Array(5)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const revenueDollars = ((summary?.estimatedRevenueCents ?? 0) / 100).toFixed(2);

  const cards = [
    {
      label: 'Total Arches',
      value: summary?.totalArches ?? 0,
      icon: Package,
      iconColor: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
    {
      label: 'Published',
      value: summary?.publishedArches ?? 0,
      icon: CheckCircle,
      iconColor: 'text-green-500',
      bgColor: 'bg-green-500/10',
    },
    {
      label: 'Total Invocations',
      value: (summary?.totalInvocations ?? 0).toLocaleString(),
      icon: Zap,
      iconColor: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
    },
    {
      label: 'Est. Revenue',
      value: `$${revenueDollars}`,
      icon: DollarSign,
      iconColor: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10',
    },
    {
      label: 'Avg Fitness',
      value: `${((summary?.avgFitness ?? 0) * 100).toFixed(0)}%`,
      icon: TrendingUp,
      iconColor: 'text-amber-500',
      bgColor: 'bg-amber-500/10',
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
      {cards.map((card) => {
        const IconComponent = card.icon;
        return (
          <Card key={card.label} className={card.bgColor}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{card.label}</p>
                  <p className="text-2xl font-bold">{card.value}</p>
                </div>
                <div className={card.iconColor}>
                  <IconComponent className="h-8 w-8 opacity-80" />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
